/**
 * Frivillige (PEOPLE.md, fane 2).
 *
 * ---------------------------------------------------------------------------------------------
 * THE ANSWER SUMMARY IS THE HARD PART, AND IT IS WHY THIS FILE LOOKS THE WAY IT DOES.
 *
 * «Oppsummering av skjemasvar … skal være helt datadrevet. Ingenting av dette hardkodes.» So no
 * schema here names a t-shirt size, a department or a day. A signup carries a LIST of answers,
 * each naming the field it answered; the summary carries a LIST of panels, each naming the field
 * it summarises and the rule the field's type implies:
 *
 *   select / multiselect / checkbox / department / day → `buckets`, one per option
 *   number                                             → `buckets`, one per range the API grouped
 *   date                                               → `buckets`, one per day
 *   text / textarea / file                             → no buckets, only `answered`
 *
 * A field type the summary has no rule for cannot be added to a form — see the enum.
 *
 * ---------------------------------------------------------------------------------------------
 * NOTHING HERE HAS A NAME FIELD. Every volunteer is a reference to Contacts, resolved for display
 * by the query — the same rule the crew list and the shift plan already obey.
 */
import { z } from 'zod';

import { cursorQuerySchema, page } from '../http/pagination.js';

import { resolvedAccreditationLevelSchema } from './accreditations.js';
import { personSystemKeySchema } from './person-standard-fields.js';
import { rowViewerSchema } from './row-viewer.js';
import { signupFormKindSchema, signupKindSchema, supplierRefSchema } from './suppliers.js';

// --------------------------------------------------------------------------------------- enums

export const VOLUNTEER_STATUSES = ['pending', 'approved', 'rejected', 'withdrawn'] as const;
export const volunteerStatusSchema = z.enum(VOLUNTEER_STATUSES);
export type VolunteerStatus = z.infer<typeof volunteerStatusSchema>;

export const VOLUNTEER_SOURCES = ['form', 'manual'] as const;
export const volunteerSourceSchema = z.enum(VOLUNTEER_SOURCES);
export type VolunteerSource = z.infer<typeof volunteerSourceSchema>;

export const SIGNUP_FORM_STATUSES = ['planned', 'open', 'closed'] as const;
export const signupFormStatusSchema = z.enum(SIGNUP_FORM_STATUSES);
export type SignupFormStatus = z.infer<typeof signupFormStatusSchema>;

export const SIGNUP_FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'date',
  'select',
  'multiselect',
  'checkbox',
  'file',
  'heading',
  'department',
  'day',
  'shift',
] as const;
export const signupFieldTypeSchema = z.enum(SIGNUP_FIELD_TYPES);
export type SignupFieldType = z.infer<typeof signupFieldTypeSchema>;

/**
 * Which types the summary counts by OPTION, as one list both sides read.
 *
 * Exported rather than repeated: the API decides what to aggregate and the page decides what to
 * draw, and the two disagreeing would show a bar chart of a field nobody counted.
 */
export const COUNTED_FIELD_TYPES = [
  'select',
  'multiselect',
  'checkbox',
  'department',
  'day',
] as const;

/** Types that carry their own `options` list. The rest either have none or take them from the year. */
export const OPTION_FIELD_TYPES = ['select', 'multiselect'] as const;

// --------------------------------------------------------------------------------------- forms

export const signupFieldDtoSchema = z
  .object({
    id: z.uuid(),
    formId: z.uuid(),
    key: z.string(),
    label: z.string(),
    help: z.string().nullable(),
    type: signupFieldTypeSchema,
    required: z.boolean(),
    /** Null for every type that has no list of its own — see the table. */
    options: z.array(z.string()).nullable(),
    sortOrder: z.number().int(),
    /**
     * PERSONFELTET skjemafeltet ER (0920), eller null for et rent skjemasvar («Nytt spørsmål»).
     *
     * Koblingen er id-en, ikke nøkkelen: «Tskjorte-størrelse» og «T-skjortestørrelse» møtes nå
     * fordi noen valgte feltet, ikke fordi de tilfeldigvis staves likt. Svaret skrives på personen.
     */
    personFieldId: z.uuid().nullable(),
    /** Standardfeltet personfeltet er — skjemaet tegner kontrollen etter den. */
    systemKey: personSystemKeySchema.nullable(),
  })
  .meta({ id: 'SignupField' });
export type SignupFieldDto = z.infer<typeof signupFieldDtoSchema>;

export const signupFormDtoSchema = z
  .object({
    id: z.uuid(),
    editionId: z.uuid(),
    key: z.string(),
    name: z.string(),
    intro: z.string().nullable(),
    status: signupFormStatusSchema,
    opensAt: z.iso.datetime().nullable(),
    closesAt: z.iso.datetime().nullable(),
    maxResponses: z.number().int().positive().nullable(),
    locale: z.string(),
    createsContact: z.boolean(),
    notifyDepartmentId: z.uuid().nullable(),
    requiresApproval: z.boolean(),
    /**
     * The link's secret half — «offentlig lenke per skjema, med kopier-knapp og QR-kode».
     *
     * It IS on the organiser's DTO, because the organiser is the one who publishes the link, and
     * `signup_form:list` is admin-and-member only. Nothing anonymous ever reads this shape.
     */
    publicToken: z.string(),
    /** «antall åpninger» and «antall svar» — the two halves of «fullføringsgrad». */
    viewCount: z.number().int().nonnegative(),
    responseCount: z.number().int().nonnegative(),
    fields: z.array(signupFieldDtoSchema),
    /** 1096: frivillig eller organisasjon (leverandøren med ansatte). Menyen grupperer på den. */
    kind: signupFormKindSchema,
    /** 1096: fristen kontaktpersonen kan rette de ansatte fram til. Bare for organisasjon. */
    memberDeadline: z.iso.datetime().nullable(),
  })
  .meta({ id: 'SignupForm' });
export type SignupFormDto = z.infer<typeof signupFormDtoSchema>;

export const signupFormListResponseSchema = z
  .object({ items: z.array(signupFormDtoSchema) })
  .meta({ id: 'SignupFormListResponse' });
export type SignupFormListResponse = z.infer<typeof signupFormListResponseSchema>;

export const listSignupFormsQuerySchema = z
  .object({ editionId: z.uuid() })
  .meta({ id: 'ListSignupFormsQuery' });
export type ListSignupFormsQuery = z.infer<typeof listSignupFormsQuerySchema>;

// ----------------------------------------------------------------------------------- signups

/** På påmeldingen (1160, 290.5): avlyst når, og hvor mange vakter «Angre» kan legge tilbake. */
export const volunteerCancellationSchema = z
  .object({
    cancelledAt: z.iso.datetime(),
    shiftCount: z.number().int().nonnegative(),
  })
  .meta({ id: 'VolunteerCancellation' });
export type VolunteerCancellation = z.infer<typeof volunteerCancellationSchema>;

/**
 * Ett svar, som raden det er.
 *
 * `value` is `unknown` rather than a union, and that is the contract being honest: the shape is
 * decided by the FIELD, which the API validates against, and a union here would have to be widened
 * every time a field type is added. The page reads it through the field's own type.
 */
export const signupAnswerSchema = z
  .object({
    fieldId: z.uuid(),
    key: z.string(),
    label: z.string(),
    type: signupFieldTypeSchema,
    value: z.unknown(),
  })
  .meta({ id: 'SignupAnswer' });
export type SignupAnswer = z.infer<typeof signupAnswerSchema>;

export const volunteerDtoSchema = z
  .object({
    id: z.uuid(),
    editionId: z.uuid(),
    personId: z.uuid(),
    contactId: z.uuid().nullable(),
    /**
     * Om kontaktraden står i organisasjonens register (K-14).
     *
     * Usann for en frivillig som meldte seg på selv: raden bærer navnet, e-posten og telefonen,
     * men står ikke i Kontakter før noen løfter den. Det er dette feltet panelet leser for å vite
     * om «Løft til kontakt» skal stå der. Usann også når det ikke finnes en kontaktrad i det hele
     * tatt — da er det ingenting å løfte.
     */
    inRegister: z.boolean(),
    displayName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    /** Null on a manual entry, and the source column says which — never guess from the null. */
    formId: z.uuid().nullable(),
    formName: z.string().nullable(),
    status: volunteerStatusSchema,
    source: volunteerSourceSchema,
    submittedAt: z.iso.datetime(),
    decidedAt: z.iso.datetime().nullable(),
    consentAt: z.iso.datetime().nullable(),
    accreditationLevelId: z.uuid().nullable(),
    accreditationLevelName: z.string().nullable(),
    /**
     * NIVÅET PÅ ARMBÅNDET (215.21), og det lista viser og teller. Feltene over er påmeldingens eget
     * valg og det den skriver; dette er hva akkrediteringen faktisk gir, og hvor det kom fra.
     */
    resolvedLevel: resolvedAccreditationLevelSchema,
    /** «antall tildelte skift» — counted, never stored. A `declined` shift is not one of them. */
    shiftCount: z.number().int().nonnegative(),
    /**
     * Inline, because the list's columns ARE the form's questions: «ønsket avdeling» and
     * «tilgjengelige dager» are answers, not columns this contract could name. One request draws
     * the whole table whatever the organiser asked.
     */
    answers: z.array(signupAnswerSchema),
    /** 1096: firmaet når påmeldingen er en leverandørs. Null for en frivillig. */
    supplier: supplierRefSchema.nullable(),
    /** 1160 (290.5): avlyst — siden er stengt, og «Angre» kan legge vaktene tilbake. Null ellers. */
    cancellation: volunteerCancellationSchema.nullable().optional(),
    /** Hva leseren kan gjøre med raden (D1), regnet av `can()` på serveren. */
    viewer: rowViewerSchema.optional(),
  })
  .meta({ id: 'Volunteer' });
export type VolunteerDto = z.infer<typeof volunteerDtoSchema>;

export const VOLUNTEER_SORT_KEYS = ['submitted', 'name', 'status'] as const;
export type VolunteerSortKey = (typeof VOLUNTEER_SORT_KEYS)[number];

export const listVolunteersQuerySchema = cursorQuerySchema(VOLUNTEER_SORT_KEYS)
  .extend({
    editionId: z.uuid(),
    status: volunteerStatusSchema.optional(),
    source: volunteerSourceSchema.optional(),
    formId: z.uuid().optional(),
    /** 1096: Frivillige (standard) eller Leverandører. */
    kind: signupKindSchema.optional(),
    /** Name or email. The same «søk» the crew list has. */
    q: z.string().trim().min(1).max(120).optional(),
  })
  .meta({ id: 'ListVolunteersQuery' });
export type ListVolunteersQuery = z.infer<typeof listVolunteersQuerySchema>;

export const volunteerListResponseSchema = page(volunteerDtoSchema, 'VolunteerListResponse');
export type VolunteerListResponse = z.infer<typeof volunteerListResponseSchema>;

// ------------------------------------------------------------------------------- one person

/** «Vakter personen er satt opp på, med timer og bekreftelsesstatus.» */
export const volunteerShiftSchema = z
  .object({
    shiftId: z.uuid(),
    title: z.string(),
    roleName: z.string().nullable(),
    departmentName: z.string().nullable(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    status: z.enum(['assigned', 'confirmed', 'declined']),
  })
  .meta({ id: 'VolunteerShift' });
export type VolunteerShift = z.infer<typeof volunteerShiftSchema>;

/** «Historikk fra tidligere årganger (antall skift, hvilke avdelinger).» */
export const volunteerHistorySchema = z
  .object({
    editionId: z.uuid(),
    editionName: z.string(),
    year: z.number().int(),
    shiftCount: z.number().int().nonnegative(),
    departments: z.array(z.string()),
  })
  .meta({ id: 'VolunteerHistory' });
export type VolunteerHistory = z.infer<typeof volunteerHistorySchema>;

export const volunteerNoteSchema = z
  .object({
    id: z.uuid(),
    body: z.string(),
    createdAt: z.iso.datetime(),
    createdBy: z.uuid().nullable(),
    createdByName: z.string().nullable(),
  })
  .meta({ id: 'VolunteerNote' });
export type VolunteerNote = z.infer<typeof volunteerNoteSchema>;

/**
 * Personpanelet, i ett kall.
 *
 * «Panelet er stedet man gjør vurderingen 'passer denne personen på denne vakta', så alt som
 * trengs for det skal være der uten videre klikking.» Five requests to open one panel is exactly
 * the further clicking that sentence forbids, one layer down.
 */
export const volunteerDetailSchema = volunteerDtoSchema
  .extend({
    shifts: z.array(volunteerShiftSchema),
    history: z.array(volunteerHistorySchema),
    notes: z.array(volunteerNoteSchema),
  })
  .meta({ id: 'VolunteerDetail' });
export type VolunteerDetail = z.infer<typeof volunteerDetailSchema>;

// -------------------------------------------------------------------------------- the summary

export const summaryBucketSchema = z
  .object({
    /** The option, the range («1990–1999») or the day. Already a label — the API groups. */
    label: z.string(),
    count: z.number().int().nonnegative(),
  })
  .meta({ id: 'SummaryBucket' });
export type SummaryBucket = z.infer<typeof summaryBucketSchema>;

export const fieldSummarySchema = z
  .object({
    fieldId: z.uuid(),
    key: z.string(),
    label: z.string(),
    type: signupFieldTypeSchema,
    /** Standardfeltet, når feltet er ett: bøttene er da koder («gluten») som skjermen oversetter. */
    systemKey: personSystemKeySchema.nullable(),
    /** How many signups answered at all — the denominator, and the whole story for free text. */
    answered: z.number().int().nonnegative(),
    /** Empty for the types that are not counted. Never null: an empty panel is still a panel. */
    buckets: z.array(summaryBucketSchema),
  })
  .meta({ id: 'FieldSummary' });
export type FieldSummary = z.infer<typeof fieldSummarySchema>;

export const volunteerSummarySchema = z
  .object({
    /** «antall påmeldte, godkjente, ventende … og hvor mange som har fått skift». */
    total: z.number().int().nonnegative(),
    approved: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    /** «med alder på eldste ubehandlede» — days, not a date, because that is what is read. */
    oldestPendingDays: z.number().int().nonnegative().nullable(),
    withShifts: z.number().int().nonnegative(),
    fields: z.array(fieldSummarySchema),
  })
  .meta({ id: 'VolunteerSummary' });
export type VolunteerSummary = z.infer<typeof volunteerSummarySchema>;

export const volunteerSummaryQuerySchema = z
  .object({
    editionId: z.uuid(),
    formId: z.uuid().optional(),
    /** 1096: Frivillige (standard) eller Leverandører. */
    kind: signupKindSchema.optional(),
  })
  .meta({ id: 'VolunteerSummaryQuery' });
export type VolunteerSummaryQuery = z.infer<typeof volunteerSummaryQuerySchema>;

// --------------------------------------------------------------------------------- the writes

/**
 * «LEGG TIL MANUELT» MED EN NY PERSON (persontypene §3.3, 1090): navn, e-post og telefon, uten at
 * personen må finnes i Kontakter først. Personen havner ikke i Kontakter av den grunn
 * (`in_register = false`), som en lenkeperson.
 */
export const newVolunteerPersonSchema = z
  .object({
    givenName: z.string().trim().min(1).max(60),
    familyName: z.string().trim().max(60).optional(),
    email: z.email().max(200).optional(),
    phone: z.string().trim().max(40).optional(),
  })
  .meta({ id: 'NewVolunteerPerson' });
export type NewVolunteerPerson = z.infer<typeof newVolunteerPersonSchema>;

export const createVolunteerRequestSchema = z
  .object({
    editionId: z.uuid(),
    /** En kontakt organisasjonen har (fra Kontakter eller en tidligere årgang). */
    personId: z.uuid().optional(),
    /** 1090: eller en ny person, skrevet inn i fanen. */
    newPerson: newVolunteerPersonSchema.optional(),
    status: volunteerStatusSchema.optional(),
    /** 1090: send den personlige lenken på e-post med det samme. */
    sendLink: z.boolean().optional(),
  })
  .refine((value) => (value.personId === undefined) !== (value.newPerson === undefined), {
    message: 'Give either personId or newPerson.',
    path: ['personId'],
  })
  .refine(
    (value) =>
      value.sendLink !== true ||
      value.newPerson === undefined ||
      value.newPerson.email !== undefined,
    {
      message: 'A link is sent by e-mail.',
      path: ['newPerson', 'email'],
    },
  )
  .meta({ id: 'CreateVolunteerRequest' });
export type CreateVolunteerRequest = z.infer<typeof createVolunteerRequestSchema>;

export const updateVolunteerRequestSchema = z
  .object({
    status: volunteerStatusSchema.optional(),
    accreditationLevelId: z.uuid().nullish(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateVolunteerRequest' });
export type UpdateVolunteerRequest = z.infer<typeof updateVolunteerRequestSchema>;

/** How many rows one bulk operation may touch. A screenful of a paged list, with room to spare. */
export const MAX_BULK_VOLUNTEERS = 200;

/**
 * «Masseoperasjoner på valgte rader: godkjenn, tildel skift, sett akkrediteringsnivå.»
 *
 * ONE ROUTE WITH A DISCRIMINATED BODY rather than three, because the three share everything that
 * matters: the same selection, the same permission, the same all-or-nothing transaction. «Send
 * melding» is the fourth in the spec and is not here — messaging is its own surface with its own
 * cells, and reaching it from a checkbox list would be a second way into it.
 */
export const BULK_VOLUNTEER_ACTIONS = ['status', 'accreditation', 'shift'] as const;

/**
 * ONE FLAT OBJECT WITH A `superRefine`, not a discriminated union, and that is a tooling
 * constraint rather than a modelling preference: `createZodDto` builds a class from the schema for
 * Nest and OpenAPI, and a union has no statically known members to build one from. The refinement
 * gives the same guarantee at the same edge — an `action: 'shift'` with no `shiftId` is refused by
 * the parser, with the path naming the missing field.
 */
export const bulkVolunteerRequestSchema = z
  .object({
    action: z.enum(BULK_VOLUNTEER_ACTIONS),
    ids: z.array(z.uuid()).min(1).max(MAX_BULK_VOLUNTEERS),
    status: volunteerStatusSchema.optional(),
    /** Nullable AND optional: null is «ta bort nivået», absent is «denne handlingen gjelder ikke». */
    accreditationLevelId: z.uuid().nullish(),
    shiftId: z.uuid().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.action === 'status' && value.status === undefined) {
      ctx.addIssue({ code: 'custom', path: ['status'], message: 'A status is required.' });
    }
    if (value.action === 'accreditation' && value.accreditationLevelId === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['accreditationLevelId'],
        message: 'A level is required, or null to clear it.',
      });
    }
    if (value.action === 'shift' && value.shiftId === undefined) {
      ctx.addIssue({ code: 'custom', path: ['shiftId'], message: 'A shiftId is required.' });
    }
  })
  .meta({ id: 'BulkVolunteerRequest' });
export type BulkVolunteerRequest = z.infer<typeof bulkVolunteerRequestSchema>;

export const bulkVolunteerResponseSchema = z
  .object({
    /** How many rows the operation actually changed. A no-op is a truthful zero, not an error. */
    changed: z.number().int().nonnegative(),
  })
  .meta({ id: 'BulkVolunteerResponse' });
export type BulkVolunteerResponse = z.infer<typeof bulkVolunteerResponseSchema>;

export const createVolunteerNoteRequestSchema = z
  .object({ body: z.string().trim().min(1).max(4000) })
  .meta({ id: 'CreateVolunteerNoteRequest' });
export type CreateVolunteerNoteRequest = z.infer<typeof createVolunteerNoteRequestSchema>;
